import { getTenantFormConfig } from "./form";
import GenericForm from "../GenericForm";
import { TenantApi, TenantDataType } from "@/api/TenantApi";
import { useEffect, useRef, useState } from "react";
import { DynamicFormRef } from "@mavichpe/dynamic-forms";
import { TenantFormData } from "@/formTypes/TenantDataType";
import { ClientApi, ClientDataType } from "@/api/ClientApi";
import LoadingScreen from "@components/content/LoadingScreen";
import { createSuscription } from "@/api/SuscriptionsApi";
import { CurrencyApi, CurrencyDataType } from "@/api/CurrencyApi";
import { ActivityCodeApi, ActivityCodeDataType } from "@/api/ActivityCodeApi";
import { RoleApi } from "@/api/RoleApi";
import { omitProperties } from "@utils/Utils";
import { ApiClient } from "@/api/ApiClient";
import { ONVO_TENANT_PAYMENT_TYPE_CODE } from "@utils/Constants";
import { buildAllDefaultEmailTemplates, buildDefaultAdminRole, buildDefaultClient, buildDefaultCurrency } from "@amplifyShared/tenantDefaults";

type TenantFormProps = {
  id?: string;
  formId: string,
  saveRedirectUrl?: string,
  copyData?: boolean,
  onStored?: (tenantData?: TenantDataType) => void
}

const TenantForm = (props: TenantFormProps) => {
  const { id: tenantId } = props
  const [clients, setClients] = useState<ClientDataType[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const formRef = useRef<DynamicFormRef>(null);

  useEffect(() => {
    setIsLoading(true)
    loadDefaultData()
  }, [])

  const loadDefaultData = async () => {
    try {
      const { items: allClients } = await ClientApi.fetchAll();
      setClients(allClients);

      if (!tenantId || props.copyData) {
        const nextCodigo = await TenantApi.getNextCode("numero")
        setTimeout(() => {
          formRef.current?.setFormValue("multiple", {
            numero: nextCodigo,
            createTenant: true
          }, "multipleData")
        })
      }
    } finally {
      setIsLoading(false)
    }
  }

  const getForm = () => {
    return getTenantFormConfig(!tenantId || !!props.copyData)
  }

  const onInputUpdate = (name: keyof TenantFormData, value: any, event?: React.ChangeEvent<any>) => {
    if (name === "clientId") {
      const client = clients.find(c => c.id === value);
      if (client?.id) {
        formRef.current?.setFormValue("multiple", {
          nombre: client.nombre,
          clientId: client.id
        }, "multipleData")
      }
    }
  }

  const createTenant = async (data: TenantDataType): Promise<TenantDataType> => {
    const { createTenant = false } = data as TenantFormData;
    let suscriptionId;
    if (createTenant) {
      const defaultTemplates = buildAllDefaultEmailTemplates({
        nombre: data.nombre,
        telefono1: data.telefono1,
        email: data.email,
      })

      const suscriptionData = {
        numero: data.numero,
        nombre: data.nombre,
        activo: true,
        pricemanaged: "1",
        disabled_acl: true,
        owner: data.tenant,
        ...defaultTemplates,
      }
      const { data: suscription, errors } = await createSuscription(suscriptionData)
      suscriptionId = `${suscription?.id || ""}`
      if (errors) {
        throw Error("No fue posible crear la suscription")
      }

      const clientContado: ClientDataType = {
        ...buildDefaultClient(),
        owner: data.tenant
      }
      await ClientApi.create(clientContado)

      const moneda: CurrencyDataType = {
        ...buildDefaultCurrency(),
        owner: data.tenant,
      }
      await CurrencyApi.create(moneda)

      const actividadEconomica: ActivityCodeDataType = {
        code: data.codigoActividad,
        title: data.nombreActividad,
        activo: true,
        default: true,
        owner: data.tenant,
      }
      await ActivityCodeApi.create(actividadEconomica)

      await RoleApi.create({
        ...buildDefaultAdminRole(),
        id: `${data.tenant}-admin`,
        owner: data.tenant,
      })
    } else {
      suscriptionId = data.suscriptionId
    }

    let tenantData: TenantDataType = {
      ...(omitProperties(data, ["createTenant"])),
      owner: data.tenant,
      suscriptionId: suscriptionId
    }
    if (data.tipoPago == ONVO_TENANT_PAYMENT_TYPE_CODE) {
      tenantData = await upsertOnvoCustomer(tenantData)
    }

    const tenant = TenantApi.create(tenantData)
    return tenant;
  }

  const updateTenant = async (id: string, data: TenantDataType): Promise<TenantDataType> => {
    let tenantData = data
    if (data.tipoPago == ONVO_TENANT_PAYMENT_TYPE_CODE) {
      tenantData = await upsertOnvoCustomer(data)
    }
    const updatedTenant = TenantApi.update(id, tenantData)
    return updatedTenant
  }

  const upsertOnvoCustomer = async (data: TenantDataType): Promise<TenantDataType> => {
    const { data: response, errors } = await ApiClient.mutations.upsertOnvoCustomerMutation({
      onvoCustomerId: data.onvoCustomerId,
      name: `${data.nombre}`,
      email: `${data.email}`,
      phone: `+${data.telefono1_cd}${data.telefono1}`
    })

    if ((response?.error || []).length > 0) {
      throw new Error("No fue posible crear/actualizar el cliente en Onvo")
    }

    const customer = response?.customer
    return {
      ...data,
      onvoCustomerId: customer?.id
    }
  }

  return (
    <>
      <GenericForm<TenantFormData, TenantDataType>
        id={props.id}
        copyData={props.copyData}
        ref={formRef}
        formId={props.formId}
        onStored={props.onStored}
        onInputChange={onInputUpdate}
        saveRedirectUrl={props.saveRedirectUrl}
        formConfig={getForm()}
        getById={(...arg) => TenantApi.get(...arg)}
        copyExcludeProps={["numero"]}
        createFn={(...arg) => createTenant(...arg)}
        updateFn={(...arg) => updateTenant(...arg)}
        successMessage="La suscripcion ha sido almacenada correctamente"
        errorMessage="No fue posible almacenar la suscripcion"
      />
      <LoadingScreen isLoading={isLoading} />
    </>
  )
}

export default TenantForm;